import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import './camera.scss';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import CameraSelector from './CameraSelector';
import { calculateCamSize } from './CameraBlock';
import CameraView from './CameraView';
import EditCameraPTZ from './EditCameraPTZ';
import Switches from '../../../components/fields/Switches/Switches';
import notificationDecorator, { errorNotification } from '../../Notifications/notificationDecorator';
import { loadCameras, nodeEvent } from '../../Settings/actions';
import { getResponseError } from '../../../utils/utils';

export default function Camera(props) {
  const {
    cameraIndex,
    cameraList,
    cameraWidth,
    cameraHeight,
    cameraBlock,
    frameWidth,
    frameHeight,
    selectedViewType,
    setCamEdit = () => {},
    selectCamera = () => {},
    selectedCameraId = null,
    hideSettings = false,
    fwd = -32,
    fhd = -82,
    anchor,
    customComponent = null,
    position = null,
    loadData = () => {},
    selectZonePolygon = () => {},
    polygon = [],
    onNodeEvent = null,
    nodeEventValue = null,
  } = props;
  const intl = useIntl();
  const { formatMessage } = intl;
  const [isEdit, setIsEdit] = useState(false);
  const [cameraStyle, setStyle] = useState({});
  const cameraRef = useRef(null);
  const dispatch = useDispatch();
  const camera = cameraList.find(c => c.id === selectedCameraId);
  useEffect(() => {
    if (isEdit) {
      calculateBlock();
      setStyle(calculateBlock());
    }
  }, [frameHeight, frameWidth, selectedViewType]);
  const calculateBlock = () => {
    if (!cameraBlock || !cameraBlock.current || !cameraRef || !cameraRef.current) {
      return;
    }
    const blockRect = cameraBlock.current.getBoundingClientRect();
    const cameraRect = cameraRef.current.getBoundingClientRect();
    const { width, height } = calculateCamSize(frameWidth + fwd, frameHeight + fhd, 0);
    const marginLeft = (anchor === 'left' ? (frameWidth - width) : (blockRect.width - width)) / 2;
    return {
      width,
      height,
      left: (blockRect.left - cameraRect.left) + marginLeft,
      top: blockRect.top - cameraRect.top + (blockRect.height - height) / 2,
      zIndex: 999,
      backLeft: (blockRect.left - cameraRect.left),
      backTop: blockRect.top - cameraRect.top,
      backWidth: frameWidth,
      backHeight: frameHeight,
    };
  };
  const handleEditCam = () => {
    if (isEdit) {
      setStyle({});
      setIsEdit(false);
      setCamEdit(false);
      return;
    }
    if (!cameraBlock || !cameraBlock.current || !cameraRef || !cameraRef.current) {
      return;
    }
    setStyle(calculateBlock());
    setIsEdit(true);
    setCamEdit(true);
  };
  window.handleEditCam = handleEditCam;
  const handleSelectCamera = id => {
    selectCamera(id);
  };

  const handleNodeEvent = e => {
    const unpause = e.target.value;
    if (onNodeEvent) {
      onNodeEvent(unpause);
      return;
    }

    dispatch(nodeEvent({ unpause, id: camera.id }))
      .then(() => {
        dispatch(loadCameras());
      })
      .catch(response => {
        errorNotification(formatMessage({ id: unpause ? 'camera.node.events.unpause' : 'camera.node.events.pause' }), getResponseError(response));
      });
  };

  const cameraOptions = cameraList.map(c => ({ value: c.id, name: c.name, status: c.status }));
  const CustomComponent = customComponent || EditCameraPTZ;
  const hasAllNodeControl = Boolean(onNodeEvent);
  const hasAllNodeControlState = typeof nodeEventValue === 'boolean';
  return (
    <div
      className={classNames('camera', `camera-${cameraIndex}`, { 'is-edit': isEdit })}
      style={{
        width: `${cameraWidth}px`,
        height: `${cameraHeight}px`,
      }}
      ref={cameraRef}
    >
      <div
        className="camera-back"
        style={{
          width: `${cameraStyle.backWidth || 0}px`,
          height: `${cameraStyle.backHeight || 0}px`,
          left: `${cameraStyle.backLeft || 0}px`,
          top: `${cameraStyle.backTop || 0}px`,
        }}
      />
      <div className="camera-wrapper" style={cameraStyle}>
        <div className="camera-buttons">
          <CameraSelector disabled={isEdit} selected={selectedCameraId} onSelect={handleSelectCamera} options={cameraOptions} />
          {camera && !hideSettings ? (<div onClick={handleEditCam} className="camera-settings"><Icons customIcon icon={isEdit ? 'close' : 'settings'} className="arrow-icon" /></div>) : null}
          {camera && !hideSettings && (!hasAllNodeControl || hasAllNodeControlState) ? (
            <Switches
              label=""
              inputProps={{
                value: hasAllNodeControl ? nodeEventValue : camera.name,
                onChange: handleNodeEvent,
              }}
            />
          ) : null}
        </div>
        <CameraView selectedCamera={camera} isEdit={isEdit} />
        {isEdit && camera ? (
          <CustomComponent
            key={selectedCameraId}
            loadData={loadData}
            position={position}
            camera={camera}
            width={cameraStyle.width || 0}
            height={cameraStyle.height || 0}
            selectZonePolygon={p => { handleEditCam(); selectZonePolygon(p); }}
            polygon={polygon}
          />
        ) : null}
      </div>
    </div>
  );
}
